/**
 * Job-service definition endpoints for odl_app.
 *
 * Job services are listed via EntityGrid (dataSource/apiClient) for the main
 * grid view; this module injects the write mutations (create / patch / remove)
 * and a full-list query used by dropdowns and the child-services dialog. The
 * service-types reference list is also exposed as a cached query.
 *
 * Contains:
 * - jobServicesApi      — the injected endpoint slice
 * - hook re-exports     — useListJobServicesQuery, useLazyListJobServicesQuery,
 *                         useListServiceTypesQuery,
 *                         useCreateServiceMutation, usePatchServiceMutation,
 *                         useRemoveServiceMutation
 */

import { entitiesApi } from './entitiesApi';

const jobServicesApi = entitiesApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Full list of job service definitions.
     *
     * Used by dialogs that need an in-memory list (child-services picker,
     * new-price service dropdown, work-form panel options). Pass { limit }
     * to cap the result set; the default returns all records.
     *
     * @param {{ limit?: number, [key: string]: any }} [params]
     */
    listJobServices: build.query({
      query: (params = {}) => ({
        url: 'services/',
        grid: Object.keys(params).length ? { filters: params } : undefined,
      }),
      // Normalize to {results, count} to match existing caller expectations.
      transformResponse: b => ({ results: b.data ?? [], count: b.meta?.total }),
      providesTags: [{ type: 'JobService', id: 'LIST' }],
    }),

    /**
     * Reference list of service type strings (e.g. 'site', 'panel').
     * Cached for the session — types rarely change.
     *
     * @returns {{ data: string[] }}
     */
    listServiceTypes: build.query({
      query: () => ({ url: 'services/types/' }),
      transformResponse: b => b.data ?? [],
      keepUnusedDataFor: 3600,
      providesTags: [{ type: 'JobService', id: 'TYPES' }],
    }),

    /**
     * Create a new job service definition.
     *
     * @param {{ body: object }} arg
     */
    createService: build.mutation({
      query: ({ body }) => ({
        url: 'services/',
        method: 'post',
        data: body,
      }),
      transformResponse: b => b.data,
      invalidatesTags: [{ type: 'JobService', id: 'LIST' }],
    }),

    /**
     * Partial update a job service definition.
     *
     * @param {{ id: string|number, body: object }} arg
     */
    patchService: build.mutation({
      query: ({ id, body }) => ({
        url: `services/${id}/`,
        method: 'patch',
        data: body,
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'JobService', id: 'LIST' },
        { type: 'JobService', id },
      ],
    }),

    /**
     * Delete a job service definition.
     *
     * @param {{ id: string|number }} arg
     */
    removeService: build.mutation({
      query: ({ id }) => ({
        url: `services/${id}/`,
        method: 'delete',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'JobService', id: 'LIST' },
        { type: 'JobService', id },
      ],
    }),
  }),
});

export { jobServicesApi };
export const {
  useListJobServicesQuery,
  useLazyListJobServicesQuery,
  useListServiceTypesQuery,
  useCreateServiceMutation,
  usePatchServiceMutation,
  useRemoveServiceMutation,
} = jobServicesApi;
